<template>
  <q-layout view="hHh Lpr lFf" class="kiosk-mode">
    <q-page-container>
      <router-view />
    </q-page-container>
  </q-layout>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useKioskAuth } from '@/composables/useKioskAuth';
import { useKioskInterceptor } from '@/composables/useKioskInterceptor';
import { getConfig } from '@/config';

const router = useRouter();
const { kioskKey } = useKioskAuth();
const {
  setupKioskAuthInterceptor,
  setupKioskUnauthorizedInterceptor,
  removeKioskAuthInterceptor,
  removeKioskUnauthorizedInterceptor,
} = useKioskInterceptor();

let reloadTimeoutId: ReturnType<typeof setTimeout> | null = null;

const redirectToPairingIfNoKey = () => {
  const current = router.currentRoute.value;
  if (!kioskKey.value && current.name !== 'kiosk-pairing') {
    router.replace({
      name: 'kiosk-pairing',
      query: { next: current.fullPath },
    });
  }
};

const scheduleReloadFromSelfConfig = async () => {
  if (!kioskKey.value) return;
  try {
    const { TF_HUB_BACKEND_URL, TENANT } = getConfig();
    // eslint-disable-next-line no-restricted-syntax -- hand-rolled binding until AGW kiosk self-config is in the OpenAPI spec
    const { data } = await axios.get<{
      data: { reloadIntervalMinutes: number | null };
    }>(`${TF_HUB_BACKEND_URL}/${TENANT}/kiosk/self-config`);
    const minutes = data.data.reloadIntervalMinutes;
    if (reloadTimeoutId !== null) clearTimeout(reloadTimeoutId);
    if (minutes && minutes > 0) {
      reloadTimeoutId = setTimeout(() => globalThis.location.reload(), minutes * 60_000);
      console.log(`[kiosk] auto-reload scheduled in ${minutes} min from now`);
    }
  } catch (e) {
    console.warn('[kiosk] could not fetch self-config', e);
  }
};

onMounted(async () => {
  setupKioskAuthInterceptor();
  setupKioskUnauthorizedInterceptor();
  redirectToPairingIfNoKey();
  await scheduleReloadFromSelfConfig();
});

watch(kioskKey, async (key) => {
  redirectToPairingIfNoKey();
  if (key) {
    await scheduleReloadFromSelfConfig();
  } else if (reloadTimeoutId !== null) {
    clearTimeout(reloadTimeoutId);
    reloadTimeoutId = null;
  }
});

onUnmounted(() => {
  if (reloadTimeoutId !== null) clearTimeout(reloadTimeoutId);
  removeKioskAuthInterceptor();
  removeKioskUnauthorizedInterceptor();
});
</script>

<style lang="scss" scoped>
.kiosk-mode {
  background: var(--q-dark);
}
:deep(.q-page-container) {
  padding: 0 !important;
}
</style>
